<template>
  <section class="rounded-lg border border-slate-200">
    <div class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
      <div>
        <h4 class="text-sm font-semibold text-slate-900">选择批次</h4>
        <p class="mt-0.5 text-xs text-slate-500">只展示当前合同下可查询批次，提交时以后端状态校验为准。</p>
      </div>
      <button type="button" class="btn-secondary" :disabled="!contractId || loading" @click="loadBatches">刷新</button>
    </div>
    <div class="data-table-shell border-0 shadow-none">
      <div class="data-table-scroll">
        <table class="data-table text-sm">
          <thead class="data-table-head">
            <tr>
              <th class="data-table-th text-left w-10">选择</th>
              <th class="data-table-th text-left">批次编号</th>
              <th class="data-table-th text-right">剩余本金</th>
              <th class="data-table-th text-right">年利率（%）</th>
              <th class="data-table-th text-left">到期日</th>
              <th v-if="mode === 'decrease'" class="data-table-th text-right">减资金额</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td :colspan="mode === 'decrease' ? 6 : 5" class="data-table-empty">加载中...</td>
            </tr>
            <tr v-else-if="rows.length === 0">
              <td :colspan="mode === 'decrease' ? 6 : 5" class="data-table-empty">暂无可选批次</td>
            </tr>
            <tr v-for="row in rows" v-else :key="row.id" class="data-table-row">
              <td class="data-table-td">
                <input
                  :type="mode === 'decrease' ? 'checkbox' : 'radio'"
                  class="form-check"
                  :checked="isSelected(row.id)"
                  @change="toggleRow(row, $event)"
                >
              </td>
              <td class="data-table-td font-mono text-xs text-slate-900">{{ row.batchNo }}</td>
              <td class="data-table-td is-right">¥{{ formatMoney(row.remainingPrincipalAmount) }}</td>
              <td class="data-table-td is-right">{{ formatRate(row.annualRate) }}</td>
              <td class="data-table-td">{{ formatDate(row.maturityDate) }}</td>
              <td v-if="mode === 'decrease'" class="data-table-td is-right">
                <input
                  :value="amountMap[row.id] || ''"
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-input form-control-sm w-32 text-right"
                  :disabled="!isSelected(row.id)"
                  @input="updateAmount(row.id, $event.target.value)"
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div v-if="mode === 'decrease'" class="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-sm">
      <span class="text-slate-500">已选 {{ selectedItems.length }} 个批次</span>
      <span class="font-medium text-slate-900">合计减资：¥{{ formatMoney(totalAmount) }}</span>
    </div>
  </section>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { getWealthContractBatchPage } from '@/api/wealth'
import { formatDate, formatMoney, formatRate, toNumber } from '../utils'

const props = defineProps({
  contractId: {
    type: Number,
    default: null
  },
  mode: {
    type: String,
    default: 'decrease'
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  selectedBatchId: {
    type: Number,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'update:selectedBatchId', 'select-row'])

const rows = ref([])
const loading = ref(false)
const selectedIds = ref([])
const amountMap = reactive({})

const selectedItems = computed(() => selectedIds.value.map(id => ({
  batchId: id,
  amount: toNumber(amountMap[id])
})).filter(item => item.batchId))

const totalAmount = computed(() => selectedItems.value.reduce((sum, item) => sum + toNumber(item.amount), 0))

async function loadBatches() {
  if (!props.contractId) {
    rows.value = []
    return
  }
  loading.value = true
  try {
    const result = await getWealthContractBatchPage(props.contractId, { page: 1, size: 200, status: 'ACTIVE' })
    rows.value = result?.records || []
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}

function isSelected(id) {
  return props.mode === 'decrease' ? selectedIds.value.includes(id) : props.selectedBatchId === id
}

function toggleRow(row, event) {
  if (props.mode === 'decrease') {
    if (event.target.checked) {
      selectedIds.value = [...new Set([...selectedIds.value, row.id])]
      if (!amountMap[row.id]) amountMap[row.id] = row.remainingPrincipalAmount
    } else {
      selectedIds.value = selectedIds.value.filter(id => id !== row.id)
      delete amountMap[row.id]
    }
    emit('update:modelValue', selectedItems.value)
    return
  }
  emit('update:selectedBatchId', row.id)
  emit('select-row', row)
}

function updateAmount(id, value) {
  amountMap[id] = value
  emit('update:modelValue', selectedItems.value)
}

watch(() => props.modelValue, value => {
  if (props.mode !== 'decrease') return
  selectedIds.value = (value || []).map(item => item.batchId)
  Object.keys(amountMap).forEach(key => delete amountMap[key])
  ;(value || []).forEach(item => {
    amountMap[item.batchId] = item.amount
  })
}, { immediate: true, deep: true })

watch(() => props.contractId, () => {
  selectedIds.value = []
  Object.keys(amountMap).forEach(key => delete amountMap[key])
  emit('update:modelValue', [])
  loadBatches()
}, { immediate: true })
</script>
